import { useState, useCallback, useEffect } from 'react';
import { useAuth } from 'context/auth/Auth';
import eventsApi from 'api/requests/events';
import establishmentsApi from 'api/requests/establishments';
import { useShowLoginModal } from 'context/general/ShowLoginModal';
import { useFavoritedEventsOptional } from 'context/events/FavoritedEvents';

const STATUS_FAVORITE = 4;
const STATUS_INTEREST = 2;

// Module-level store so all instances sharing the same slug stay in sync
const _store = new Map();        // slug → boolean
const _listeners = new Map();    // slug → Set<setState>
const _toggledSlugs = new Set(); // slugs manually toggled this session (user choice wins over API)

function _notify(slug, value) {
    _store.set(slug, value);
    _listeners.get(slug)?.forEach(fn => fn(value));
}

function _subscribe(slug, setState) {
    if (!_listeners.has(slug)) _listeners.set(slug, new Set());
    _listeners.get(slug).add(setState);
    return () => _listeners.get(slug)?.delete(setState);
}

/**
 * @param {string} slug
 * @param {boolean} initialFavorited
 * @param {object|null} itemData
 * @param {'event'|'establishment'} type - which API to call
 */
export default function useFavorite(slug, initialFavorited = false, itemData = null, type = 'event') {
    const { isAuth } = useAuth();
    const { setShowLoginModal } = useShowLoginModal();
    const favCtx = useFavoritedEventsOptional();

    // If the user hasn't manually toggled this slug, always trust the API value.
    // This ensures the button reflects the real favorite state on every render.
    if (!_toggledSlugs.has(slug)) {
        _store.set(slug, initialFavorited);
    }

    const [isFavorited, setIsFavorited] = useState(() => _store.get(slug));
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        // Sync local state with store (covers slug changes and post-toggle renders)
        setIsFavorited(_store.get(slug));
        return _subscribe(slug, setIsFavorited);
    }, [slug]);

    const toggleFavorite = useCallback(async () => {
        if (!isAuth) {
            setShowLoginModal(true);
            return;
        }

        if (loading) return;

        const newValue = !_store.get(slug);
        const previousValue = _store.get(slug);
        const newStatus = newValue ? STATUS_FAVORITE : STATUS_INTEREST;

        _toggledSlugs.add(slug); // user choice now wins over API initialFavorited
        _notify(slug, newValue);

        // Optimistic context update (only for events)
        if (type === 'event') {
            if (newValue && itemData) favCtx?.addFavorite(itemData);
            else if (!newValue) favCtx?.removeFavorite(slug);
        }

        setLoading(true);

        try {
            if (type === 'establishment') {
                await establishmentsApi.interest(slug, newStatus);
            } else {
                await eventsApi.interest(slug, newStatus);
            }
        } catch (error) {
            _notify(slug, previousValue);
            // Rollback context update
            if (type === 'event') {
                if (newValue) favCtx?.removeFavorite(slug);
                else if (itemData) favCtx?.addFavorite(itemData);
            }
        } finally {
            setLoading(false);
        }
    }, [isAuth, loading, slug, setShowLoginModal, itemData, favCtx, type]);

    return { isFavorited, toggleFavorite, loading };
}
